import {FC} from 'react'
import {Trans, useTranslation} from '@/translations/i18n.tsx'
import {errorHandler} from '@utilities/helpers.ts'
import {useQueryClient} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient.ts'
import {toast} from '@utilities/toast/toast.tsx'
import {VerificationModal} from '@/components/commons/verification-modal/VerificationModal'
import {useChangePatientStatus} from '@/features/patients/services/useChangePatientStatus'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {StyledFullName} from '@/components/commons/delete-modal/style'
import {ModalParagraph} from '@/components/ui/modal-atoms/ModalAtoms'

interface DeceasedPatientModalProps {
    onClose: () => void
    patientId: string
    patientFullName: string
}

export const DeceasedPatientModal: FC<DeceasedPatientModalProps> = ({patientId, patientFullName, onClose}) => {
    const {t} = useTranslation()
    const queryClient = useQueryClient()
    const changePatientStatusMutation = useChangePatientStatus({
        urlParams: {patientId},
        params: {responseType: 'extended'},
        options: {
            onError: errorHandler,
            onSuccess: () => {
                void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PATIENTS]})
                void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENTS]})
                toast.success(t('commons:update_success'))
                onClose()
            }
        }
    })

    return (
        <VerificationModal
            confirmationString="Deceduto"
            onClose={onClose}
            onConfirm={() => changePatientStatusMutation.mutate({status: 'deceased'})}
            isPending={changePatientStatusMutation.isPending}
            title={t('commons:patient_deceased')}
            description={
                <Flexbox direction="column" gap={4}>
                    <ModalParagraph>
                        <Trans
                            i18nKey="patients:update_status_description_modal_1"
                            values={{fullName: patientFullName}}
                            components={{span: <StyledFullName />}}
                        />
                    </ModalParagraph>
                    <ModalParagraph>{t('patients:update_status_description_modal_2')}</ModalParagraph>
                </Flexbox>
            }
        />
    )
}
